import {
  Box,
  SimpleGrid,
  Text,
  Image,
  Button as CButton,
  Icon,
  Flex,
  Tag,
  Heading,
  Divider,
  Menu,
  useDisclosure,
  MenuButton,
  MenuList,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lightbox from 'react-image-lightbox';
import { Link, useParams } from 'react-router-dom';

import 'react-image-lightbox/style.css';

import {
  IconBreakTime,
  IconBriefcase,
  IconClip,
  IconClock,
  IconComment,
  IconFileClock,
  IconMapOutline,
} from 'assets/icons';
import Button from 'components/Button';
import FallbackAvatar from 'components/FallbackAvatar';
import LogoLoader from 'components/Loader/LogoLoader';
import Title from 'components/Title';
import Tooltip from 'components/Tooltip';
import FEATURES from 'global/features';
import { CUSTOM_FIELD_DATA_TYPES } from 'global/options';
import { routePaths } from 'global/routePaths';
import { useFetchPaginatedCustomFields } from 'hooks/customField/useFetchPaginatedCustomFields';
import { useFetchTimesheetById } from 'hooks/timesheet/useFetchTimesheetById';
import { useFeatureList } from 'store/useFeatureStore';
import { formatYearMonthDayTime, formatDistance, to12Hour } from 'utils/format';
import { secondsToMS, isEmptyObject } from 'utils/helpers';

import Signatures from '../shared/Signatures';
import TimesheetEditLogs from '../shared/TimesheetEditLogs';

const TimesheetDetail: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'timesheetMileage']);

  const featureList: any = useFeatureList();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { id } = useParams();

  const [lightBox, setLightBox] = useState<{ photoIndex: number; isOpen: boolean }>({ photoIndex: 0, isOpen: false });

  const { status, data, refetch } = useFetchTimesheetById(id!);
  const { fetchStatus: customFieldFetchStatus, data: customFieldData } = useFetchPaginatedCustomFields({
    page: 1,
    page_size: 500,
    module: 'timesheets',
    active: '1',
  });

  const formatBreakTime = (breakInSeconds: number = 0) => {
    const { minutes, seconds } = secondsToMS(breakInSeconds);
    return minutes === '00' && seconds === '00' ? '0' : `${minutes}m ${seconds}s`;
  };

  if (status === 'loading' || customFieldFetchStatus === 'fetching') return <LogoLoader />;

  return (
    <Box>
      <Flex alignItems={{ md: 'center' }} flexDirection={{ base: 'column', md: 'row' }}>
        <Title>
          <Flex as="span">
            <Image
              ml="2"
              display="inline-block"
              fallback={
                <FallbackAvatar
                  name={data?.payload?.first_name + data?.payload?.last_name}
                  boxSize="32px"
                  ml="2"
                  mr="2"
                />
              }
              boxSize="32px"
              objectFit="cover"
              borderRadius="base"
              src={data?.payload?.employee_image}
              alt={data?.payload?.first_name + data?.payload?.last_name}
              mr="2"
            />
            {t('timesheetMileage:entryFor')} {data?.payload?.first_name} {data?.payload?.last_name}
          </Flex>
        </Title>
        <Text
          fontWeight="semibold"
          listStyleType="disc"
          fontSize="md"
          ml="5"
          position="relative"
          lineHeight="1.3"
          _after={{
            position: 'absolute',
            top: 'calc(62% - 4px)',
            left: '-10px',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: 'gray.300',
            content: '""',
          }}
        >
          <span>
            {data?.payload?.clock_in?.local_time ? formatYearMonthDayTime(data?.payload?.clock_in?.local_time) : null}
          </span>
          <span> - </span>
          <span>
            {data?.payload?.clock_out?.local_time ? formatYearMonthDayTime(data?.payload?.clock_out?.local_time) : null}
          </span>
        </Text>
      </Flex>
      <Flex justifyContent="flex-end" mt="2">
        <div>
          {featureList[FEATURES.photo_attachments] && (
            <Menu isLazy closeOnBlur autoSelect={false} isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
              <MenuButton
                as={CButton}
                bg="rgba(72,82, 175, 0.1)"
                color="primary.500"
                fontWeight="semibold"
                _hover={{
                  bg: 'rgba(72,82, 175, 0.2)',
                }}
                _active={{
                  bg: 'rgba(72,82, 175, 0.2)',
                }}
                leftIcon={<Icon as={IconClip} boxSize="16px" />}
              >
                {data?.payload?.attachment_files.length} {t('common:attachment(s)')}
              </MenuButton>
              <MenuList maxHeight="350px" width="305px" overflow="hidden" p="2">
                {Array.isArray(data?.payload?.attachment_files) && data?.payload?.attachment_files.length ? (
                  <Flex display="flex" flexWrap="wrap">
                    {data?.payload?.attachment_files.map((item: any, index: any) => (
                      <Tooltip label={item.caption} key={item.id}>
                        <Image
                          src={item.imageUrl}
                          alt={`attachment-${item.id}`}
                          boxSize="85px"
                          m="1"
                          objectFit="cover"
                          cursor="pointer"
                          borderRadius="6px"
                          border="1px solid"
                          borderColor="gray.300"
                          onClick={() => setLightBox({ isOpen: true, photoIndex: index })}
                        />
                      </Tooltip>
                    ))}
                    {/* </Stack> */}
                  </Flex>
                ) : null}
              </MenuList>
            </Menu>
          )}
          {featureList[FEATURES.time_signatures] ? (
            <Signatures signatures={data?.payload?.signatures} refetchData={refetch} />
          ) : null}
        </div>
        <Link to={routePaths.timesheetAndMileage.map.replace(':id', data?.payload?.id)}>
          <Button
            ml="2"
            leftIcon={<Icon as={IconMapOutline} boxSize="16px" />}
            bg="secondary.50"
            color="secondary.400"
            _hover={{
              bg: 'secondary.100',
            }}
            _active={{
              bg: 'secondary.100',
            }}
          >
            {t('timesheetMileage:viewOnMap')}
          </Button>
        </Link>
      </Flex>
      {data?.payload?.flagged_reason && (
        <Flex mt="3" alignItems="center">
          <Tag bg="secondary.100" size="lg" color="secondary.600" fontSize="sm" fontWeight="semibold">
            {t('timesheetMileage:flagged')}
          </Tag>
          <Text ml="2" color="gray.500" fontWeight="semibold">
            {data?.payload?.flagged_reason}
          </Text>
        </Flex>
      )}
      <Flex mt="10" flexWrap="wrap">
        <Flex alignItems="center" m="3" mr={{ md: '14' }}>
          <Flex
            justifyContent="center"
            alignItems="center"
            width="36px"
            height="36px"
            background="gray.100"
            borderRadius="50%"
            mr="2"
          >
            <Icon as={IconBriefcase} boxSize="16px" sx={{ '& path': { fill: 'gray.400' } }} />
          </Flex>
          <Box fontSize="sm" fontWeight="semibold">
            <Text color="gray.500" fontSize="12px" textTransform="uppercase">
              {t('common:job')}
            </Text>
            <Text>{data?.payload?.job_name}</Text>
          </Box>
        </Flex>
        <Flex alignItems="center" m="3" mr={{ md: '14' }}>
          <Flex
            justifyContent="center"
            alignItems="center"
            width="36px"
            height="36px"
            background="gray.100"
            borderRadius="50%"
            mr="2"
          >
            <Icon as={IconFileClock} boxSize="16px" sx={{ '& path': { fill: 'gray.400' } }} />
          </Flex>
          <Box fontSize="sm" fontWeight="semibold">
            <Text color="gray.500" fontSize="12px" textTransform="uppercase">
              {t('common:task')}
            </Text>
            <Text>{data?.payload?.task_name || '-'}</Text>
          </Box>
        </Flex>
        <Flex alignItems="center" m="3" mr={{ md: '14' }}>
          <Flex
            justifyContent="center"
            alignItems="center"
            width="36px"
            height="36px"
            background="gray.100"
            borderRadius="50%"
            mr="2"
          >
            <Icon as={IconClock} boxSize="16px" sx={{ '& path': { fill: 'gray.400' } }} />
          </Flex>
          <Box fontSize="sm" fontWeight="semibold">
            <Text color="gray.500" fontSize="12px" textTransform="uppercase">
              {t('common:duration')}
            </Text>
            <Text>{data?.payload?.duration}</Text>
          </Box>
        </Flex>
        <Flex alignItems="center" m="3" mr={{ md: '14' }}>
          <Flex
            justifyContent="center"
            alignItems="center"
            width="36px"
            height="36px"
            background="gray.100"
            borderRadius="50%"
            mr="2"
          >
            <Icon as={IconFileClock} boxSize="16px" sx={{ '& path': { fill: 'gray.400' } }} />
          </Flex>
          <Box fontSize="sm" fontWeight="semibold" m="3">
            <Text color="gray.500" fontSize="12px" textTransform="uppercase">
              {t('common:mileage')}
            </Text>
            <Text>{!isNaN(data?.payload?.mileage) ? formatDistance(data?.payload?.mileage) : 0}</Text>
          </Box>
        </Flex>
        <Flex alignItems="center" m="3" mr={{ md: '14' }}>
          <Flex
            justifyContent="center"
            alignItems="center"
            width="36px"
            height="36px"
            background="gray.100"
            borderRadius="50%"
            mr="2"
          >
            <Icon as={IconBreakTime} boxSize="16px" sx={{ '& path': { fill: 'gray.400' } }} />
          </Flex>
          <Box fontSize="sm" fontWeight="semibold">
            <Text color="gray.500" fontSize="12px" textTransform="uppercase">
              {t('common:break')}
            </Text>
            <Text>{formatBreakTime(data.payload?.break_in_seconds)}</Text>
          </Box>
        </Flex>
      </Flex>
      <Divider mt="10" />
      <SimpleGrid columns={{ md: 2 }} spacing={{ base: 6, md: 12 }} p="5">
        <Box>
          <Heading as="h5" size="sm" mb="4">
            {t('common:start')}
          </Heading>
          <Flex mb="2">
            <Text as="span">{t('common:time')}:</Text>
            <Text as="span" fontWeight="600" ml="1">
              {data?.payload?.clock_in?.local_time ? formatYearMonthDayTime(data?.payload?.clock_in?.local_time) : '-'}
            </Text>
          </Flex>
          <Flex mb="2">
            <Text as="span">{t('common:timezone')}:</Text>
            <Text as="span" fontWeight="600" ml="1">
              {data?.payload?.user_timezone}
            </Text>
          </Flex>
          {featureList[FEATURES.capture_start_end_addresses] && (
            <Flex mb="2">
              <Text as="span">{t('common:startAddress')}:</Text>
              <Text as="span" fontWeight="600" ml="1">
                {data?.payload?.clock_in?.address}
              </Text>
            </Flex>
          )}
          <Flex>
            <Text as="span">{t('common:inSource')}:</Text>
            <Text as="span" fontWeight="600" ml="1">
              {data?.payload?.in_source}
            </Text>
          </Flex>
        </Box>
        <Box>
          <Heading as="h5" size="sm" mb="4">
            {t('common:end')}
          </Heading>
          <Flex mb="2">
            <Text as="span">{t('common:time')}:</Text>
            <Text as="span" fontWeight="600" ml="1">
              {data?.payload?.clock_out?.local_time
                ? formatYearMonthDayTime(data?.payload?.clock_out?.local_time)
                : '-'}
            </Text>
          </Flex>
          <Flex mb="2">
            <Text as="span">{t('common:timezone')}:</Text>
            <Text as="span" fontWeight="600" ml="1">
              {data?.payload?.clock_out_timezone}
            </Text>
          </Flex>
          {featureList[FEATURES.capture_start_end_addresses] && (
            <Flex mb="2">
              <Text as="span">{t('common:endAddress')}:</Text>
              <Text as="span" fontWeight="600" ml="1">
                {data?.payload?.clock_out?.address}
              </Text>
            </Flex>
          )}
          <Flex>
            <Text as="span">{t('common:outSource')}:</Text>
            <Text as="span" fontWeight="600" ml="1">
              {data?.payload?.out_source}
            </Text>
          </Flex>
        </Box>
      </SimpleGrid>

      {data?.payload?.breaks && data?.payload?.breaks.length > 0 ? (
        <>
          <Divider mt="10" />
          <Box p="5">
            <Heading as="h5" size="sm" mb="4" textTransform="capitalize">
              {t('common:breaks')}
            </Heading>
            <SimpleGrid columns={{ md: 2 }} spacing={{ base: 6, md: 10 }}>
              {data?.payload?.breaks.map((breakItem: any) => (
                <Flex flexDirection="column" gap="1" key={breakItem.id}>
                  <Box>
                    <Text as="span" fontWeight="semibold" fontSize="md">
                      {breakItem?.break_type?.name}
                    </Text>
                  </Box>
                  <Flex>
                    <Text as="span">{t('common:time')}:</Text>
                    <Text as="span" fontWeight="600" ml="1">
                      {breakItem?.startLocal ? formatYearMonthDayTime(breakItem.startLocal) : '-'} -{' '}
                      {breakItem?.endLocal ? formatYearMonthDayTime(breakItem.endLocal) : '-'}
                    </Text>
                  </Flex>
                  <Flex>
                    <Text as="span">{t('common:duration')}:</Text>

                    <Text as="span" fontWeight="600" ml="1">
                      {formatBreakTime(breakItem?.duration_in_seconds)}
                    </Text>
                  </Flex>
                </Flex>
              ))}
            </SimpleGrid>
          </Box>
        </>
      ) : null}

      <Flex p="3" alignItems="center" bg="gray.100" color="gray.500" fontWeight="600" borderRadius="10px" mt="5">
        <Flex
          justifyContent="center"
          alignItems="center"
          width="36px"
          height="36px"
          background="white"
          borderRadius="50%"
          mr="2"
        >
          <Icon as={IconComment} boxSize="20px" sx={{ '& path': { fill: 'gray.400' } }} />
        </Flex>
        {t('common:notes')}: {data?.payload?.notes}
      </Flex>
      {data?.payload?.approved_by && (
        <>
          <Divider my="5" />
          <Box>
            <Text fontWeight="600" fontSize="12px" color="gray.500" mb="3">
              {t('common:approvedBy')}
            </Text>
            <Flex alignItems="center">
              <Image
                fallback={<FallbackAvatar name={data?.payload?.approved_by} boxSize="32px" />}
                boxSize="32px"
                objectFit="cover"
                borderRadius="base"
                src={data?.payload?.approved_by_image}
                alt={data?.payload?.approved_by}
              />
              <Text ml="2">
                {data?.payload?.approved_by}{' '}
                {data?.payload?.approved_date ? (
                  <Text as="span"> on {formatYearMonthDayTime(data?.payload?.approved_date)}</Text>
                ) : (
                  ''
                )}
              </Text>
            </Flex>
          </Box>
        </>
      )}
      {lightBox.isOpen ? (
        <Lightbox
          mainSrc={data?.payload?.attachment_files[lightBox.photoIndex].imageUrl}
          nextSrc={
            data?.payload?.attachment_files[(lightBox.photoIndex + 1) % data?.payload?.attachment_files.length].imageUrl
          }
          prevSrc={
            data?.payload?.attachment_files[
              (lightBox.photoIndex + data?.payload?.attachment_files.length - 1) %
                data?.payload?.attachment_files.length
            ].imageUrl
          }
          imageTitle={data?.payload?.attachment_files[lightBox.photoIndex].caption}
          onCloseRequest={() => setLightBox({ ...lightBox, isOpen: false })}
          onMovePrevRequest={() => {
            const newImageIndex =
              (lightBox.photoIndex + data?.payload?.attachment_files.length - 1) %
              data?.payload?.attachment_files.length;
            setLightBox({ ...lightBox, photoIndex: newImageIndex });
          }}
          onMoveNextRequest={() => {
            const newImageIndex = (lightBox.photoIndex + 1) % data?.payload?.attachment_files.length;
            setLightBox({ ...lightBox, photoIndex: newImageIndex });
          }}
          toolbarButtons={
            data?.payload?.attachment_files[lightBox.photoIndex]?.latitude &&
            data?.payload?.attachment_files[lightBox.photoIndex]?.longitude
              ? [
                  <Button
                    size="sm"
                    colorScheme="secondary"
                    onClick={() => {
                      const latitude = data?.payload?.attachment_files[lightBox.photoIndex]?.latitude;
                      const longitude = data?.payload?.attachment_files[lightBox.photoIndex]?.longitude;

                      const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
                      window.open(url, '_blank');
                    }}
                    mr="4"
                  >
                    View Location
                  </Button>,
                ]
              : []
          }
        />
      ) : null}

      {/* Custom Fields */}
      {!isEmptyObject(data?.payload?.custom_fields || {}) ? (
        <Box p="5">
          <Heading as="h5" size="sm" mb="4">
            Custom Fields
          </Heading>
          <SimpleGrid columns={{ md: 2 }} spacingX={{ base: 6, md: 12 }}>
            {customFieldData?.payload.map(field => {
              let customFieldValue = data?.payload?.custom_fields[field.id];

              if (field.field_type === CUSTOM_FIELD_DATA_TYPES.TIME_PICKER) {
                customFieldValue = to12Hour(customFieldValue);
              }

              if (customFieldValue) {
                return (
                  <Flex mb="2" key={field.id}>
                    <Text as="span">{field.field_name}:</Text>
                    <Text as="span" fontWeight="600" ml="1">
                      {Array.isArray(customFieldValue) ? customFieldValue.join(', ') : customFieldValue}
                    </Text>
                  </Flex>
                );
              }

              return null;
            })}
          </SimpleGrid>
        </Box>
      ) : null}
      <TimesheetEditLogs updateLogs={data?.payload?.update_logs} />
    </Box>
  );
};

export default TimesheetDetail;
